/**
 * ⚖️ BALANCE CONFIG - Core game balance constants
 *
 * ALL tunable game numbers in ONE place!
 * Edit this file to adjust game feel without touching other code.
 *
 * ROI = Return on Investment (time for building to "pay for itself")
 * Target ROIs:
 *   Tier 1: 20-30 seconds
 *   Tier 2-3: 45-90 seconds
 *   Tier 4-5: 2-4 minutes
 *   Tier 6-7: 5-10 minutes
 *   Tier 8+: 15+ minutes
 */

export const BALANCE_CONFIG = {
  // ===========================================
  // 🎬 STARTING STATE
  // ===========================================
  startingResources: {
    wood: 15,       // Reduced from 25 - slower start
    stone: 5,
    food: 5,        // Reduced from 10
    gold: 5,
    iron: 5,
    population: 0,
    knowledge: 3,   // Reduced from 5 - can't rush first tech
    culture: 0,
    steel: 0,
    coal: 0,
    gems: 0,
    tools: 0,
    machinery: 0,
    science: 0,
    energy: 0,
    magic: 0,
    artifacts: 0,
    crystals: 0,
  },

  // ===========================================
  // 🏗️ BUILDING SCALING
  // ===========================================
  buildings: {
    costMultiplier: 2.0,      // 2x all building costs (slower progression)
    costScale: 1.20,          // 20% more expensive per owned building
    maxPerType: 999,          // Max buildings of each type
    refundPercent: 0.5,       // 50% refund when selling (future feature)

    // Tile-Based Capacity System
    // Each building placed on a world map tile grants capacity for panel purchases
    capacityPerTile: 5,       // Each tile building allows 5 panel buildings of same type
    requiresTileForPanel: true, // Must build on tile before panel purchases allowed
  },

  // ===========================================
  // 🔬 TECHNOLOGY - RESEARCH TIME & COST SYSTEM
  // ===========================================
  tech: {
    // Base research times in SECONDS by tier
    // PACING: Smooth curve with no sudden walls
    // Target: 30+ hour full playthrough
    // T6→T7 is now 1.67x jump (was 2.5x - major fix)
    baseTimes: {
      1: 45,       // 45 sec - learning the ropes
      2: 120,      // 2 min - still slow
      3: 300,      // 5 min - building foundation
      4: 360,      // 6 min - starts speeding up
      5: 480,      // 8 min - momentum building
      6: 720,      // 12 min - slight increase for pacing (was 10min)
      7: 1200,     // 20 min - SMOOTHED (was 25min - major fix)
      8: 1800,     // 30 min - reduced (was 40min)
      9: 2700,     // 45 min - slight reduction (was 50min)
      10: 3600,    // 60 min - reduced (was 70min)
      11: 5400,    // 90 min - preserved for satisfying endgame grind
    },

    // Per-tier COST multipliers
    // Values > 1.0 = more expensive, < 1.0 = cheaper
    // Affects all resource costs (knowledge + secondary)
    // Creates natural "checkpoint" gates at T5, T7, T9, T11
    tierCostMultipliers: {
      1: 0.85,     // 15% cheaper (early game generous)
      2: 0.90,     // 10% cheaper
      3: 1.0,      // baseline
      4: 1.0,      // baseline
      5: 1.10,     // 10% more expensive (first gate)
      6: 1.15,     // 15% more expensive
      7: 1.25,     // 25% more expensive (mid-game gate)
      8: 1.30,     // 30% more expensive
      9: 1.40,     // 40% more expensive (late-game gate)
      10: 1.50,    // 50% more expensive
      11: 1.75,    // 75% more expensive (final challenge)
    },

    // Global cost multiplier (stacks with tier multipliers)
    globalCostMultiplier: 1.0,

    // Research speed multiplier (affected by upgrades/buildings)
    baseSpeedMultiplier: 1.0,
    // Minimum time for any research (prevents instant research)
    minResearchTime: 10,
  },

  // ===========================================
  // 👆 CLICKING / MANUAL GATHERING - WITH UPGRADES
  // ===========================================
  clicking: {
    baseValue: 4,             // Starting +1 resource per click
    cooldownMs: 100,           // 50ms between clicks (20 clicks/sec max - anti-macro)
    critChance: 0.05,         // 5% chance for critical gather
    critMultiplier: 2,        // 2x resources on crit (was 3x)
    bonusKnowledge: 0.003,    // +0.003 knowledge per click (reduced from 0.005)
  },

  // ===========================================
  // 👆 CLICK POWER UPGRADES - Milestone based
  // ===========================================
  clickUpgrades: [
    // { id, name, icon, clickPower, cost, description }
    // Balanced assuming ~20 clicks/sec autoclicker = 20 * clickPower per second
    // Should be worse than equivalent buildings but still useful
    {
      id: 'click_1',
      name: 'Wooden Tools',
      icon: '🪓',
      clickPower: 2,  // +2 per click (was 1)
      cost: { wood: 50 },
      description: 'Basic tools. +2 per click.'
    },
    {
      id: 'click_2',
      name: 'Stone Tools',
      icon: '⛏️',
      clickPower: 3,
      cost: { wood: 150, stone: 100 },
      description: 'Sharper edges. +3 per click.'
    },
    {
      id: 'click_3',
      name: 'Iron Tools',
      icon: '🔧',
      clickPower: 5,
      cost: { iron: 100, gold: 50 },
      description: 'Durable metal. +5 per click.'
    },
    {
      id: 'click_4',
      name: 'Steel Tools',
      icon: '⚒️',
      clickPower: 8,
      cost: { steel: 100, gold: 200 },
      description: 'Master crafted. +8 per click.'
    },
    {
      id: 'click_5',
      name: 'Enchanted Tools',
      icon: '✨',
      clickPower: 12,
      cost: { steel: 200, gems: 50, magic: 25 },
      description: 'Magically enhanced. +12 per click.'
    },
    {
      id: 'click_6',
      name: 'Legendary Artifacts',
      icon: '👑',
      clickPower: 20,
      cost: { artifacts: 10, magic: 100, gems: 100 },
      description: 'Weapons of legend. +20 per click.'
    },
  ],

  // ===========================================
  // 🔬 RESEARCH SPEED UPGRADES
  // ===========================================
  researchUpgrades: [
    {
      id: 'research_1',
      name: 'Organized Notes',
      icon: '📝',
      speedBonus: 0.1,  // +10% research speed
      cost: { knowledge: 20 },
      description: '+10% research speed'
    },
    {
      id: 'research_2',
      name: 'Study Groups',
      icon: '👥',
      speedBonus: 0.15,  // +15% (total 25%)
      cost: { knowledge: 75, gold: 50 },
      description: '+15% research speed'
    },
    {
      id: 'research_3',
      name: 'Library Access',
      icon: '📚',
      speedBonus: 0.20,  // +20% (total 45%)
      cost: { knowledge: 200, culture: 50 },
      description: '+20% research speed'
    },
    {
      id: 'research_4',
      name: 'Scientific Method',
      icon: '🔬',
      speedBonus: 0.25,  // +25% (total 70%)
      cost: { knowledge: 500, science: 50 },
      description: '+25% research speed'
    },
    {
      id: 'research_5',
      name: 'Arcane Insights',
      icon: '🔮',
      speedBonus: 0.30,  // +30% (total 100% = 2x speed)
      cost: { knowledge: 1000, magic: 100 },
      description: '+30% research speed'
    },
    // Advanced upgrades for 30+ hour playtime - extends max speed to 3.05x
    {
      id: 'research_6',
      name: 'Collaborative Networks',
      icon: '🌐',
      speedBonus: 0.35,  // +35% (total 135%)
      cost: { knowledge: 5000, science: 300 },
      unlockTech: 'printing',
      description: '+35% research speed'
    },
    {
      id: 'research_7',
      name: 'Think Tanks',
      icon: '🧠',
      speedBonus: 0.40,  // +40% (total 175%)
      cost: { knowledge: 15000, science: 1000 },
      unlockTech: 'scientific_method',
      description: '+40% research speed'
    },
    {
      id: 'research_8',
      name: 'Transcendent Knowledge',
      icon: '💭',
      speedBonus: 0.30,  // +30% (total 205% = 3.05x speed)
      cost: { knowledge: 40000, magic: 3000, science: 2000 },
      unlockTech: 'enchanting',
      description: '+30% research speed (Max)'
    },
  ],

  // ===========================================
  // ⏱️ TICK RATES
  // ===========================================
  ticks: {
    production: 100,          // Production tick every 100ms (smoother bars)
    productionMultiplier: 0.1,// Multiply production by this (since 10 ticks/sec)
    autosave: 30000,          // Autosave every 30 seconds
    achievementCheck: 5000,   // Check achievements every 5 seconds
  },

  // ===========================================
  // 📈 PRODUCTION MULTIPLIERS
  // ===========================================
  multipliers: {
    global: 1.0,              // Global production multiplier
    perPopulation: 0.001,     // +0.1% production per population
    maxPopulationBonus: 0.5,  // Cap at +50% from population
  },

  // ===========================================
  // 🎯 PROGRESSION TARGETS (for balancing)
  // ===========================================
  targets: {
    // Time in MINUTES to reach milestone
    // Total target: 20+ hours (1200+ minutes)
    firstBuilding: 0.5,       // 30 seconds
    fiveBuildings: 3,         // 3 minutes
    firstTech: 4,             // 4 minutes
    tenBuildings: 8,          // 8 minutes
    unlockStone: 10,          // 10 minutes (Era 2)
    unlockIron: 45,           // 45 minutes (Era 4)
    midGame: 180,             // 3 hours (Era 5-6)
    lateGame: 480,            // 8 hours (Era 7-8)
    endGame: 900,             // 15 hours (Era 9-10)
    victory: 1200,            // 20 hours (Era 11 complete)
  },

  // ===========================================
  // 🏰 KINGDOM VISUAL THRESHOLDS
  // ===========================================
  kingdom: {
    // Building count to reach each visual tier
    tier1: 10,                 // Small Village
    tier2: 30,                // Growing Town
    tier3: 60,                // Prosperous Town
    tier4: 100,                // Thriving City
    tier5: 150,                // Grand City
    tier6: 200,               // Major Kingdom
    tier7: 300,               // Mighty Empire
    tier8: 400,               // Glorious Empire
    tier9: 600,               // Legendary Empire
    tier10: 1000,              // Wonder of the World
  },

  // ===========================================
  // 📜 QUEST BALANCE - Per-Chapter Scaling
  // ===========================================
  quests: {
    // Global multipliers (applied to all chapters)
    globalRewardMultiplier: 1.0,    // Multiply all quest rewards
    globalObjectiveMultiplier: 1.0, // Multiply count-based objectives
    xpPerQuest: 100,                // Future: XP system

    // Per-chapter reward multipliers (stacks with global)
    // Values > 1.0 = more generous, < 1.0 = harder
    // Mid-game chapters (3-6) get bonuses to prevent grind feeling
    // Chapter 10 is a victory lap with max bonus
    chapterRewardMultipliers: {
      1: 1.0,   // Awakening - standard
      2: 1.0,   // Settlement - standard
      3: 1.15,  // Iron Age - feels rewarding (+15%)
      4: 1.20,  // Commerce - bonus (+20%)
      5: 1.25,  // Industry - most generous mid-game hook (+25%)
      6: 1.15,  // Power - bonus (+15%)
      7: 1.10,  // Empire - slight bonus (+10%)
      8: 1.0,   // Science - back to standard
      9: 1.0,   // Magic - standard
      10: 1.30, // Ascension - victory lap, most generous (+30%)
    },

    // Per-chapter objective multipliers (for count-based objectives only)
    // Values > 1.0 = harder (more to do), < 1.0 = easier
    chapterObjectiveMultipliers: {
      1: 1.0,
      2: 1.0,
      3: 1.0,
      4: 1.0,
      5: 1.0,
      6: 1.0,
      7: 1.0,
      8: 1.0,
      9: 1.0,
      10: 1.0,
    },
  },

  // Base advisor slots
  baseAdvisorSlots: 2,

  // Advisor slot upgrades (tech-gated)
  advisorSlotUpgrades: [
    {
      id: 'advisor_slot_3',
      name: 'Expanded Council',
      description: 'Add a third seat to your advisory council',
      slots: 3,
      cost: { gold: 500, culture: 100 },
      unlockTech: 'philosophy',
    },
    {
      id: 'advisor_slot_4',
      name: 'Grand Council',
      description: 'Establish a grand council with four advisors',
      slots: 4,
      cost: { gold: 2000, culture: 400, knowledge: 300 },
      unlockTech: 'monarchy',
    },
    {
      id: 'advisor_slot_5',
      name: 'Supreme Council',
      description: 'The ultimate advisory body with five advisors',
      slots: 5,
      cost: { gold: 8000, artifacts: 100, crystals: 50 },
      unlockTech: 'arcane_arts',
    },
  ],

  // ===========================================
  // 🎖️ ACHIEVEMENTS
  // ===========================================
  achievements: {
    notificationDuration: 5000, // Show achievement popup for 5 seconds
  },

  // ===========================================
  // 🎁 ANTI-FRUSTRATION MECHANICS
  // Inspired by Cookie Clicker's "golden cookie" system
  // ===========================================
  antiFrustration: {
    // "Golden" resource events - random bonus resources
    goldenEvents: {
      enabled: true,
      checkInterval: 300000,   // Check every 5 minutes
      chance: 0.12,            // 12% chance per check
      bonusRange: [0.08, 0.20], // 8-20% of current stockpile
      resources: ['gold', 'food', 'wood', 'stone'],
      notification: true,
      messages: [
        '🌟 A golden shimmer! Your stockpiles mysteriously grow.',
        '✨ Fortune smiles upon your kingdom!',
        '🎁 The gods bestow a gift upon your realm.',
        '💫 A burst of prosperity!',
      ],
    },

    // Exploration warning before hitting limit
    explorationWarning: {
      enabled: true,
      warningThreshold: 0.90,  // Warn at 90% of limit
      message: 'Your explorers are nearing their limits! Research more techs to expand.',
    },
  },
};

export default BALANCE_CONFIG;
